export const ACTIVE_CALL_SELECTION_KEY = "m360.activeCallSelection";
export const ACTIVE_CALL_DRAFT_KEY = "m360.activeCallDraft";
export const DISMISSED_ACTIVE_CALL_MODAL_CALL_SIDS_KEY = "m360.dismissedActiveCallPersonModalCallSids";
export const ACTIVE_CALL_SESSION_CHANGED_EVENT = "m360:activeCallSessionChanged";

export type ActiveCallSelection = {
  callSid: string;
  personId?: string;
  pgaId?: string;
  ended?: boolean;
};

export type ActiveCallDraft = {
  callSid: string;
  notes?: string;
  attachTask?: boolean;
  taskName?: string;
  taskAssignee?: {
    id: string;
    name: string;
    email: string;
  } | null;
  taskDueDate?: string | null;
  taskIsPrivate?: boolean;
};

const isActiveCallSelection = (value: unknown): value is ActiveCallSelection => {
  if (!value || typeof value !== "object") return false;

  const selection = value as Partial<ActiveCallSelection>;
  return (
    typeof selection.callSid === "string" &&
    (selection.personId === undefined || typeof selection.personId === "string") &&
    (selection.pgaId === undefined || typeof selection.pgaId === "string") &&
    (selection.ended === undefined || typeof selection.ended === "boolean")
  );
};

const isActiveCallDraft = (value: unknown): value is ActiveCallDraft => {
  if (!value || typeof value !== "object") return false;

  const draft = value as Partial<ActiveCallDraft>;
  return (
    typeof draft.callSid === "string" &&
    (draft.notes === undefined || typeof draft.notes === "string") &&
    (draft.attachTask === undefined || typeof draft.attachTask === "boolean") &&
    (draft.taskName === undefined || typeof draft.taskName === "string") &&
    (draft.taskAssignee === undefined ||
      draft.taskAssignee === null ||
      (typeof draft.taskAssignee === "object" &&
        typeof draft.taskAssignee.id === "string" &&
        typeof draft.taskAssignee.name === "string" &&
        typeof draft.taskAssignee.email === "string")) &&
    (draft.taskDueDate === undefined || draft.taskDueDate === null || typeof draft.taskDueDate === "string") &&
    (draft.taskIsPrivate === undefined || typeof draft.taskIsPrivate === "boolean")
  );
};

const getSessionStorageItem = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const removeSessionStorageItem = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {}
};

const notifyActiveCallSessionChanged = () => {
  try {
    window.dispatchEvent(new Event(ACTIVE_CALL_SESSION_CHANGED_EVENT));
  } catch {}
};

export const readActiveCallSelection = (): ActiveCallSelection | null => {
  const rawValue = getSessionStorageItem(ACTIVE_CALL_SELECTION_KEY);
  if (!rawValue) return null;

  try {
    const parsedValue: unknown = JSON.parse(rawValue);
    if (!isActiveCallSelection(parsedValue)) return null;

    return {
      callSid: parsedValue.callSid,
      ...(parsedValue.personId ? { personId: parsedValue.personId } : {}),
      ...(parsedValue.pgaId ? { pgaId: parsedValue.pgaId } : {}),
      ...(parsedValue.ended ? { ended: parsedValue.ended } : {})
    };
  } catch {
    removeSessionStorageItem(ACTIVE_CALL_SELECTION_KEY);
    return null;
  }
};

export const storeActiveCallSelection = ({ callSid, personId, pgaId, ended }: ActiveCallSelection) => {
  const selection: ActiveCallSelection = {
    callSid,
    ...(personId ? { personId } : {}),
    ...(pgaId ? { pgaId } : {}),
    ...(ended ? { ended } : {})
  };

  try {
    window.sessionStorage.setItem(ACTIVE_CALL_SELECTION_KEY, JSON.stringify(selection));
  } catch {}
  notifyActiveCallSessionChanged();
};

export const markActiveCallEndedForCallSid = (callSid: string) => {
  const selection = readActiveCallSelection();

  if (selection && selection.callSid !== callSid) return;

  storeActiveCallSelection({
    callSid,
    ...(selection?.personId ? { personId: selection.personId } : {}),
    ...(selection?.pgaId ? { pgaId: selection.pgaId } : {}),
    ended: true
  });
};

export const clearActiveCallSelectionForCallSid = (callSid: string) => {
  const selection = readActiveCallSelection();

  if (selection?.callSid === callSid) {
    removeSessionStorageItem(ACTIVE_CALL_SELECTION_KEY);
    clearActiveCallDraftForCallSid(callSid);
    notifyActiveCallSessionChanged();
  }
};

export const readActiveCallDraft = (): ActiveCallDraft | null => {
  const rawValue = getSessionStorageItem(ACTIVE_CALL_DRAFT_KEY);
  if (!rawValue) return null;

  try {
    const parsedValue: unknown = JSON.parse(rawValue);
    return isActiveCallDraft(parsedValue) ? parsedValue : null;
  } catch {
    removeSessionStorageItem(ACTIVE_CALL_DRAFT_KEY);
    return null;
  }
};

export const storeActiveCallDraft = ({
  callSid,
  notes,
  attachTask,
  taskName,
  taskAssignee,
  taskDueDate,
  taskIsPrivate
}: ActiveCallDraft) => {
  const draft: ActiveCallDraft = {
    callSid,
    ...(notes ? { notes } : {}),
    ...(attachTask !== undefined ? { attachTask } : {}),
    ...(taskName ? { taskName } : {}),
    ...(taskAssignee !== undefined ? { taskAssignee } : {}),
    ...(taskDueDate !== undefined ? { taskDueDate } : {}),
    ...(taskIsPrivate !== undefined ? { taskIsPrivate } : {})
  };

  try {
    window.sessionStorage.setItem(ACTIVE_CALL_DRAFT_KEY, JSON.stringify(draft));
  } catch {}
};

export const clearActiveCallDraftForCallSid = (callSid: string) => {
  const draft = readActiveCallDraft();

  if (draft?.callSid === callSid) {
    removeSessionStorageItem(ACTIVE_CALL_DRAFT_KEY);
  }
};

export const readDismissedActiveCallModalCallSids = (): string[] => {
  const rawValue = getSessionStorageItem(DISMISSED_ACTIVE_CALL_MODAL_CALL_SIDS_KEY);
  if (!rawValue) return [];

  try {
    const parsedValue: unknown = JSON.parse(rawValue);
    return Array.isArray(parsedValue) ? parsedValue.filter((value): value is string => typeof value === "string") : [];
  } catch {
    removeSessionStorageItem(DISMISSED_ACTIVE_CALL_MODAL_CALL_SIDS_KEY);
    return [];
  }
};

export const dismissActiveCallModalForCallSid = (callSid: string) => {
  const dismissedCallSids = new Set(readDismissedActiveCallModalCallSids());
  dismissedCallSids.add(callSid);

  try {
    window.sessionStorage.setItem(
      DISMISSED_ACTIVE_CALL_MODAL_CALL_SIDS_KEY,
      JSON.stringify(Array.from(dismissedCallSids))
    );
  } catch {}
};

export const hasDismissedActiveCallModal = (callSid: string): boolean => {
  return readDismissedActiveCallModalCallSids().includes(callSid);
};
